import type { Editor } from '@tiptap/react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const MAXIMIZE_MORPH_MS = 280;
const MAXIMIZE_MORPH_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

// Keyframe pinning a fixed box to a viewport rect. We animate top/left/width/height so the editor reflows at its natural size.
const rectKeyframe = (rect: { top: number; left: number; width: number; height: number }) => ({
  top: `${rect.top}px`,
  left: `${rect.left}px`,
  width: `${rect.width}px`,
  height: `${rect.height}px`,
});

const prefersReducedMotion = (): boolean => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

/**
 * Drives the rich-text editor's maximize toggle: lifts the editor into a modal <dialog> and morphs
 * its box between the inline rect and a viewport-filling panel. The component owns the <dialog>/<div>
 * swap in its render and the layout placeholder; this hook owns the state and the grow/shrink animation.
 */
export const useMaximize = (editor: Editor | null) => {
  const [maximized, setMaximized] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inlineRef = useRef<HTMLDivElement>(null);
  // Inline editor's rect, captured just before it lifts into the modal: seeds the grow animation and
  // the placeholder height that keeps the surrounding form from collapsing.
  const originRectRef = useRef<DOMRect | null>(null);
  // In-flight morph, so a rapid re-toggle can cancel it.
  const morphRef = useRef<Animation | null>(null);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!maximized || !dialog) {
      return;
    }

    // Prevents the first toolbar button's tooltip triggering.
    const editable = editor?.view.dom;
    if (!dialog.open && typeof dialog.showModal === 'function') {
      editable?.setAttribute('autofocus', '');
      dialog.showModal();
      editable?.removeAttribute('autofocus');
    }

    const from = originRectRef.current;
    if (!from || prefersReducedMotion() || typeof dialog.animate !== 'function') {
      return;
    }

    morphRef.current?.cancel();
    // Animate from the inline rect up to the maximized rect showModal just placed us at.
    const morph = dialog.animate([rectKeyframe(from), rectKeyframe(dialog.getBoundingClientRect())], {
      duration: MAXIMIZE_MORPH_MS,
      easing: MAXIMIZE_MORPH_EASING,
    });
    morphRef.current = morph;

    return () => {
      morphRef.current?.cancel();
    };
  }, [maximized, editor]);

  const toggleMaximize = useCallback(() => {
    if (!maximized) {
      // Capture the inline rect (seeds the grow animation + placeholder height) while still in flow.
      originRectRef.current = inlineRef.current?.getBoundingClientRect() ?? null;
      setMaximized(true);
      return;
    }

    // Shrink back to the captured inline rect, then unmount once it lands. fill: 'forwards' holds the
    // collapsed rect until React unmounts, avoiding a one-frame jump back to full size.
    const dialog = dialogRef.current;
    const target = originRectRef.current;
    if (prefersReducedMotion() || !dialog || !target || typeof dialog.animate !== 'function') {
      setMaximized(false);
      return;
    }

    morphRef.current?.cancel();
    const morph = dialog.animate([rectKeyframe(dialog.getBoundingClientRect()), rectKeyframe(target)], {
      duration: MAXIMIZE_MORPH_MS,
      easing: MAXIMIZE_MORPH_EASING,
      fill: 'forwards',
    });
    morphRef.current = morph;
    morph.finished.then(() => setMaximized(false)).catch(() => {});
  }, [maximized]);

  const closeMaximize = useCallback(() => setMaximized(false), []);

  return { maximized, toggleMaximize, closeMaximize, dialogRef, inlineRef, originRectRef };
};
